import React from 'react';
import { styled } from '@mui/material/styles';
import { renderHTML } from '@agility/nextjs';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { KitButton } from 'components/common/cms-kit';
import { useTheme } from '@mui/material/styles';

const GrayBackground = styled('div')(({ $videoAlign, $videoExists }) => ({
  padding: '40px 10px',
  '@media screen and (min-width: 900px)': {
    flexDirection: $videoAlign === 'left'
      ? $videoExists
        ? 'row-reverse'
        : 'row'
      : 'row',
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
}));

const Container = styled('div')(({ $videoAlign, $videoExists }) => ({
  position: 'relative',
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  '@media screen and (min-width: 900px)': {
    flexDirection: $videoAlign === 'left'
      ? $videoExists
        ? 'row-reverse'
        : 'row'
      : 'row',
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
}));

const MobileContainer = styled('div')({
  marginTop: '30px',
  display: 'flex',
  flexFlow: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px 0',
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
});

const ContentContainer = styled('div')(({ $videoAlign, $videoExists }) => ({
  flexGrow: 1,
  flexShrink: 0,
  flex: 1,
  verticalAlign: 'top',
  marginTop: '60px',
  width: '38%',
  '@media screen and (min-width: 900px)': {
    textAlign: $videoAlign === 'left'
      ? $videoExists
        ? 'left'
        : 'right'
      : 'right',
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
}));

const VideoContainer = styled('div')(({ $videoExists }) => ({
  flexGrow: 0,
  flexShrink: 1,
  margin: '50px 0 0 10px',
  '@media screen and (min-width: 900px)': {
    width: '62%',
    ...($videoExists ? { maxWidth: '60%' } : { display: 'none' }),
    ...($videoExists ? { maxHeight: '95%' } : { display: 'none' }),
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
}));

const MobileVideoContainer = styled('div')(({ $videoExists }) => ({
  width: '95%',
  margin: '0 0 0 20px',
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
  '@media screen and (max-width: 899px)': {
    ...($videoExists ? { width: '95%' } : { display: 'none' }),
    ...($videoExists ? { height: 'auto' } : { display: 'none' }),
  },
}));

const Title = styled('div')(({ $titleColor }) => ({
  minHeight: '25px',
  fontWeight: 600,
  margin: '15px 5px 0 5px',
  '@media screen and (min-width: 900px)': {
    color: $titleColor,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
  },
}));

const MobileTitle = styled('div')(({ $titleColor }) => ({
  fontWeight: 600,
  margin: '10px 5px',
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
  '@media screen and (max-width: 899px)': {
    color: $titleColor,
    fontSize: '1.35rem',
    lineHeight: '1.55rem',
    padding: '0 5px',
  },
}));

const SummaryDescription = styled('div')(({ $videoAlign, $videoExists, $descriptionColor }) => ({
  ...($videoAlign === 'left' ? $videoExists
      ? { margin: '30px 10% 30px 10px' }
      : { margin: '30px 10px 30px 25%' } : { margin: '30px 10px 30px 25%' }),
  fontWeight: 600,
  '@media screen and (min-width: 900px)': {
    color: $descriptionColor,
    fontSize: '2.125rem',
    lineHeight: '2.688rem',
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
}));

const MobileSummaryDescription = styled('div')(({ $descriptionColor }) => ({
  fontWeight: 600,
  width: '75%',
  margin: '10px 0 20px 0',
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
  '@media screen and (max-width: 899px)': {
    color: $descriptionColor,
    fontSize: '1.265rem',
    lineHeight: '1.45rem',
    padding: '0 5px',
  },
}));

const ButtonContainer = styled('div')({
  margin: '30px 5px',
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
});

const MobileButtonContainer = styled('div')({
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
  '@media screen and (max-width: 899px)': {
    padding: '0 25px',
  },
});

const SummaryWithVideo = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();

  const router = useRouter();

  const _callAction = (event) => {
    event.preventDefault();
    router.push(fields.actionButtonUrl.href);
  };

  const videoExists = fields.videoScript && fields.videoScript.length > 0;
  const titleColor = fields.titleColor || '#000000';
  const descriptionColor = fields.descriptionColor || '#000000';


  return (
    <React.Fragment>
      <GrayBackground
        $videoAlign={fields.videoAlign}
        $videoExists={videoExists}
        style={{
          backgroundColor: `rgba(217, 217, 217, 0.3)`,
        }}
      >
        <Box
          sx={{
            width: '98%',
            ml: 'auto',
            mr: 'auto',
            maxWidth: { sm: '540px', md: '768px', lg: '1140px', xl: '1180px' },
          }}
        >
          <Container $videoAlign={fields.videoAlign} $videoExists={videoExists}>
            <ContentContainer $videoAlign={fields.videoAlign} $videoExists={videoExists}>
              <Title $titleColor={titleColor}>{fields.title}</Title>
              <SummaryDescription $videoAlign={fields.videoAlign} $videoExists={videoExists} $descriptionColor={descriptionColor}>{fields.description}</SummaryDescription>
              {fields.actionButtonUrl &&
                fields.actionButtonUrl.href &&
                fields.actionButtonUrl.href.length > 0 && (
                  <ButtonContainer>
                    <Link
                      href={fields.actionButtonUrl.href}
                      target={fields.actionButtonUrl.target}
                      rel={`noopener noreferrer${fields.addNoFollowtoActionUrl === 'true' ? ' nofollow' : ''}`}
                    >
                      <KitButton
                        round
                        size="lg"
                        style={{
                          backgroundColor: theme.palette.obsBlue.dark,
                          weight: 6,
                        }}
                      >
                        {fields.actionButtonUrl.text}
                      </KitButton>
                    </Link>
                  </ButtonContainer>
                )}
            </ContentContainer>
            <VideoContainer $videoExists={videoExists}>
              <div
                className="prose max-w-full mx-auto"
                dangerouslySetInnerHTML={renderHTML(fields.videoScript)}
              />
            </VideoContainer>
          </Container>
        </Box>
      </GrayBackground>
      <MobileContainer>
        <MobileVideoContainer $videoExists={videoExists}>
          <div
            className="prose max-w-full mx-auto"
            dangerouslySetInnerHTML={renderHTML(fields.videoScript)}
          />
        </MobileVideoContainer>
        <MobileTitle $titleColor={titleColor}>{fields.title}</MobileTitle>
        <MobileSummaryDescription $descriptionColor={descriptionColor}>
          {fields.description}
        </MobileSummaryDescription>
        <MobileButtonContainer>
          <KitButton
            round
            style={{
              backgroundColor: theme.palette.primary.light,
            }}
            component="a"
            href={fields.actionButtonUrl.href}
            target={fields.actionButtonUrl.target}
            rel="noopener noreferrer"
          >
            {fields.actionButtonUrl.text}
          </KitButton>
        </MobileButtonContainer>
      </MobileContainer>
    </React.Fragment>
  );
};

export default SummaryWithVideo;
